import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@saiens/shared/hooks';
import { useAuthStore } from '@saiens/shared/stores';
import { ChevronDown, LogOut, User } from 'lucide-react';

function CompanySwitcher() {
  const { companies, currentCompanyId, setCompany } = useAuthStore();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const current = companies.find((c) => c.id === currentCompanyId);
  const currentName = current?.name ?? '';

  if (companies.length <= 1) {
    return (
      <h1 className="truncate text-lg font-bold tracking-tight text-foreground">
        {currentName}
      </h1>
    );
  }

  const handleSelect = (id: number) => {
    setOpen(false);
    if (id === currentCompanyId) return;
    queryClient.clear();
    setCompany(id);
    navigate('/');
  };

  return (
    <div className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex min-w-0 items-center gap-1 text-lg font-bold tracking-tight text-foreground"
      >
        <span className="truncate">{currentName}</span>
        <ChevronDown className="h-4 w-4 shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[10rem] rounded-lg border border-white/30 bg-white py-1 shadow-lg">
            {companies.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSelect(c.id)}
                className={`block w-full truncate px-3 py-2 text-left text-sm hover:bg-black/5 ${
                  c.id === currentCompanyId ? 'font-semibold text-primary' : 'text-foreground'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function Header() {
  const { logout } = useAuth();
  const { employee, lineProfile } = useAuthStore();

  return (
    <header className="glass sticky top-0 z-40 border-b border-white/30">
      <div className="flex h-14 items-center justify-between gap-3 px-4">
        <CompanySwitcher />

        <div className="flex shrink-0 items-center gap-3">
          {lineProfile?.pictureUrl ? (
            <img
              src={lineProfile.pictureUrl}
              alt={employee?.name || ''}
              className="h-8 w-8 rounded-full object-cover ring-2 ring-white/50"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <User className="h-4 w-4 text-primary" />
            </div>
          )}
          <button
            onClick={logout}
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground"
            title="登出"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
